// What a plot's planning documents say, in the shape the engines read.
//
// These come from the CU and the PUZ/PUD (typed in, or later extracted by a
// model and confirmed by a person). National defaults live in
// src/rules/ro.ts; the engine takes the stricter of the two.
import type { Derogation } from "../rules/catuc.ts";
import type { Ring } from "./geom.ts";
import type { Mass } from "./sun.ts";

/** What lies beyond an edge of the plot, which decides its setback. */
export type EdgeKind = "aliniament" | "lateral" | "posterior";

/**
 * A setback that may grow with the building: `max(min, heightRatio × H)`,
 * where H is the cornice height of the storey being placed. That is the
 * usual PUZ wording, "H/2, but no less than 3 m". A setback level is then
 * an output of the envelope, not a separate rule.
 */
export interface Setback {
  min: number;
  heightRatio?: number;
}

export interface Site {
  /** Plot boundary, metres. Edge i runs from boundary[i] to boundary[i+1]. */
  boundary: Ring;
  /** One kind per edge, in the same order. */
  edges: EdgeKind[];
  /** Degrees north, for the sun. Imported plots read it off their Stereo 70 northing. */
  latitude?: number;
  /** Neighbouring buildings (from OpenStreetMap), in the plot's local metres: they cast shadows. */
  context?: (Mass & { heightFrom?: "height" | "levels" | "assumed" })[];
}

export interface Rules {
  /** Max footprint as a percentage of plot area (procent de ocupare). */
  pot: number;
  /** Max above-ground floor area over plot area (coeficient de utilizare). */
  cut: number;
  /** Max cornice height, metres. */
  maxHeight: number;
  /** Max above-ground storeys including the ground floor, e.g. P+6E = 7. */
  maxStoreys?: number;
  setbacks: Record<EdgeKind, Setback>;
  /** Minimum green space, % of the plot, from the PUZ or the local regulation. */
  minGreen?: number;
  /** Parking spaces required per dwelling, from the PUZ or the local norm. */
  parkingPerDwelling?: number;
  /** The same by flat type, where the norm differs by size (it usually does). Overrides the flat rate. */
  parkingByType?: Partial<Record<UnitType, number>>;
  /** Shops: spaces per 100 m² of lettable area, where the PUZ or the local norm states one. Overrides the RGU steps. */
  parkingPerGla?: number;
  /**
   * Which definitions the plan was approved under. A PUZ approved before
   * 25.08.2026 keeps the old reading of POT (ground footprint only); one
   * approved since uses Legea 169/2026, where balconies and cantilevers count.
   */
  regime?: "catuc" | "legacy";
  /** The plan the indicators come from. CUT above 4 can only come from a PUG. */
  indicatorsFrom?: "PUG" | "PUZ" | "PUD";
  /** A derogation being simulated: what the plot could carry with a PUZ or a PUD. */
  derogation?: Derogation;
  /** Buildings that stay on the plot: added to the proposed ones in POT and CUT. */
  existing?: { footprint: number; gross: number };
  /** Where each value came from: "CU nr. 123/2026", "PUZ ... art. 5", ... */
  sources?: Partial<Record<"pot" | "cut" | "maxHeight" | "maxStoreys" | "setbacks" | "parking" | "green", string>>;
}

/** What the developer wants, as opposed to what the plot allows. */
export interface Programme {
  groundStoreyHeight: number;
  storeyHeight: number;
  /** Depths to try for a residential wing, metres. */
  depths: number[];
  /** Usable over gross: walls, cores, corridors are the difference. */
  efficiency: number;
  /** Balcony depth, m, and the share of each flat's frontage it spans. 0 = no balconies. */
  balconyDepth: number;
  balconyShare: number;
  /** Kitchen open to the living room (60–64% of sold flats) or closed in its own bay with a window. */
  kitchen?: "open" | "closed";
  /** Storeys allowed to step back above the main body (etaje retrase). */
  setbackStoreys: number;
  /** Ground floor use. Commercial ground floors carry no dwellings. */
  groundUse: "residential" | "commercial";
  /** Apartment mix: share of the count and target usable area. */
  mix: { type: UnitType; share: number; area: number }[];
  /**
   * A different mix for a range of storeys (0 = ground floor), e.g. smaller
   * flats low down and large ones in the setback storeys. Cores stay where
   * the main mix puts them, so they still stack.
   */
  mixByLevel?: { from: number; to: number; mix: { type: UnitType; share: number; area: number }[] }[];
  /** Where the cars go. Filled in this order: surface, podium, then basements for what is left. */
  parking: { surface: boolean; podiumLevels: number; underground: boolean };
}

export type UnitType = "1" | "2" | "3" | "4" | "5";

export const DEFAULT_PROGRAMME: Programme = {
  groundStoreyHeight: 4.0,
  storeyHeight: 3.0,
  depths: [18, 16, 14, 12],
  efficiency: 0.75,
  setbackStoreys: 2,
  balconyDepth: 1.5,
  balconyShare: 0.5,
  parking: { surface: false, podiumLevels: 0, underground: true },
  groundUse: "commercial",
  mix: [
    // usable areas: the market medians (docs/REFERENCE-PLANS.md: 41.5 / 55 / 76 / 100)
    // raised to the Legea 114 minimums where those are higher (58 for two rooms, 77 for three)
    { type: "1", share: 0.2, area: 41 },
    { type: "2", share: 0.45, area: 60 },
    { type: "3", share: 0.3, area: 80 },
    { type: "4", share: 0.05, area: 100 },
  ],
};

export function setbackAt(s: Setback, height: number): number {
  return Math.max(s.min, (s.heightRatio ?? 0) * height);
}
